/**
 * ConflictModal.tsx
 *
 * Shown automatically when sync conflicts are detected.
 * For each conflicting order the user can choose to:
 *   - Keep their local (offline) version  → force-PUTs it to the server
 *   - Keep the server version             → discards the local changes
 */

import React, { useEffect, useState } from 'react';
import { ConflictRecord } from 'utils/offlineDb';
import { UseSyncEventsResult } from 'hooks/useSyncEvents';

interface ConflictModalProps {
    conflicts: ConflictRecord[];
    resolveKeepLocal: UseSyncEventsResult['resolveKeepLocal'];
    resolveKeepServer: UseSyncEventsResult['resolveKeepServer'];
}

const ConflictModal: React.FC<ConflictModalProps> = ({
    conflicts,
    resolveKeepLocal,
    resolveKeepServer,
}) => {
    const [dismissed, setDismissed] = useState(false);
    // Unresolved conflicts stay in IndexedDB — dismissing only hides the
    // modal locally. Re-open it if the pending set actually changes (e.g. a
    // new conflict comes in) rather than leaving it stuck closed forever.
    const conflictKey = conflicts.map((c) => c.id ?? c.orderId).join(',');
    useEffect(() => {
        setDismissed(false);
    }, [conflictKey]);

    if (conflicts.length === 0 || dismissed) return null;

    return (
        // Backdrop
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setDismissed(true)}
        >
            <div
                 role="dialog"
                 aria-modal="true"
                 aria-label="Conflits de synchronisation"
                 className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
                 onClick={(e) => e.stopPropagation()}
             >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 bg-amber-50">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">⚠️</span>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {conflicts.length} Sync Conflict{conflicts.length > 1 ? 's' : ''} Detected
                            </h2>
                            <p className="text-sm text-gray-500">
                                Some orders were modified on the server while you were offline.
                                Choose which version to keep for each one.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setDismissed(true)}
                        aria-label="Fermer"
                        className="shrink-0 text-xl leading-none text-gray-400 hover:text-gray-600 px-2"
                    >
                        ×
                    </button>
                </div>

                {/* Conflict list */}
                <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
                    {conflicts.map((conflict) => (
                        <ConflictRow
                            key={conflict.id ?? conflict.orderId}
                            conflict={conflict}
                            onKeepLocal={() => resolveKeepLocal(conflict)}
                            onKeepServer={() => resolveKeepServer(conflict)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Individual conflict row ──────────────────────────────────────────────────

interface ConflictRowProps {
    conflict: ConflictRecord;
    onKeepLocal: () => void;
    onKeepServer: () => void;
}

const ConflictRow: React.FC<ConflictRowProps> = ({ conflict, onKeepLocal, onKeepServer }) => {
    const orderRef =
        conflict.localVersion?.reference ||
        conflict.serverVersion?.reference ||
        conflict.orderId;

    const localDate = conflict.mutation.createdAt
        ? new Date(conflict.mutation.createdAt).toLocaleString()
        : '—';

    const serverDate = conflict.serverVersion?.updatedAt
        ? new Date(conflict.serverVersion.updatedAt).toLocaleString()
        : '—';

    return (
        <div className="px-6 py-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="font-semibold text-gray-800">Order #{orderRef}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                        Detected {new Date(conflict.detectedAt).toLocaleString()}
                    </p>
                </div>
            </div>

            {/* Side-by-side comparison */}
            <div className="mt-3 grid grid-cols-2 gap-3">
                {/* Local version */}
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">
                        Your offline version
                    </p>
                    <p className="text-xs text-gray-500">Edited {localDate}</p>
                    <VersionSummary data={conflict.localVersion} />
                    <button
                        onClick={onKeepLocal}
                        className="mt-3 w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 transition-colors"
                    >
                        Keep my version
                    </button>
                </div>

                {/* Server version */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                        Server version
                    </p>
                    <p className="text-xs text-gray-500">Updated {serverDate}</p>
                    <VersionSummary data={conflict.serverVersion} />
                    <button
                        onClick={onKeepServer}
                        className="mt-3 w-full rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold py-2 transition-colors"
                    >
                        Keep server version
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Compact data summary ─────────────────────────────────────────────────────

const DISPLAY_FIELDS: { key: string; label: string }[] = [
    { key: 'status', label: 'Status' },
    { key: 'totalTTC', label: 'Total TTC' },
    { key: 'deliveryDate', label: 'Delivery date' },
    { key: 'note', label: 'Note' },
];

const VersionSummary: React.FC<{ data: any }> = ({ data }) => {
    if (!data) return <p className="text-xs text-gray-400 mt-2">No data</p>;

    const items = DISPLAY_FIELDS.filter(({ key }) => data[key] !== undefined);

    if (items.length === 0) return <p className="text-xs text-gray-400 mt-2">—</p>;

    return (
        <dl className="mt-2 space-y-0.5">
            {items.map(({ key, label }) => (
                <div key={key} className="flex gap-1.5 text-xs">
                    <dt className="text-gray-400 min-w-[80px]">{label}:</dt>
                    <dd className="text-gray-700 font-medium truncate">
                        {String(data[key]).substring(0, 40)}
                    </dd>
                </div>
            ))}
        </dl>
    );
};

export default ConflictModal;
